// Trusted systems — the registry of applications allowed to call /api/v1.
//
// Registering one used to be a hand-written INSERT, which made the platform's
// most security-relevant write the one with the least validation and no audit.
// This page is the superadmin surface for it.
//
// The design choice worth naming: the JWKS probe. Onboarding otherwise fails
// at the far end of a long loop — register the row, deploy the calling app,
// watch every request 401 — and the API deliberately does not say whether the
// issuer was unknown or the signature bad. "Check keys" does the fetch the
// verifier would do and reports what it found, including the `kid` values,
// which is the field integrators most often get wrong.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Input,
  Label,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { checkTrustedSystem, createTrustedSystem, deleteTrustedSystem } from "@/lib/api/endpoints";
import { useTrustedSystems } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { TrustedSystem, TrustedSystemCheckResult } from "@/types/api";

export default function TrustedSystemsPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useTrustedSystems();
  const [showForm, setShowForm] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<TrustedSystem | null>(null);
  // Probe results, keyed by identifier. Kept on the page rather than in the
  // query cache: a probe is a point-in-time observation, not state of the row,
  // and showing a stale "reachable" after a JWKS rotation would mislead.
  const [checks, setChecks] = React.useState<Record<string, TrustedSystemCheckResult>>({});

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.trustedSystems });

  const remove = useMutation({
    mutationFn: (row: TrustedSystem) => deleteTrustedSystem(row.id),
    onSuccess: async (_r, row) => {
      await invalidate();
      setPendingDelete(null);
      toast.success(`${row.identifier} revoked`);
    },
    onError: (e: unknown) => toast.error(errorText(e)),
  });

  // Keyed by identifier for display, addressed by id on the wire — an OIDC
  // issuer is a URL and cannot be a path segment.
  const check = useMutation({
    mutationFn: (row: TrustedSystem) => checkTrustedSystem(row.id),
    onSuccess: (result, row) => setChecks((c) => ({ ...c, [row.identifier]: result })),
    onError: (e: unknown) => toast.error(errorText(e)),
  });

  return (
    <div>
      <PageHeader
        title="Trusted systems"
        description="Applications allowed to call the runtime API. Each row maps an inbound token's issuer to the keys Aviary verifies it against."
        actions={
          <Button onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            Register application
          </Button>
        }
      />

      {showForm ? (
        <RegisterForm
          onDone={async () => {
            await invalidate();
            setShowForm(false);
          }}
        />
      ) : null}

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Card className="p-4">
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title="No applications registered"
          hint="Nothing can call /api/v1 until an issuer is registered here."
          action={<Button onClick={() => setShowForm(true)}>Register application</Button>}
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Identifier (iss)</TH>
                <TH>Name</TH>
                <TH>Keys</TH>
                <TH className="w-px" />
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((row) => (
                <TR key={row.id}>
                  <TD className="font-mono text-xs">{row.identifier}</TD>
                  <TD>{row.name}</TD>
                  <TD>
                    <KeySource row={row} result={checks[row.identifier]} />
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={check.isPending}
                        onClick={() => check.mutate(row)}
                      >
                        <KeyRound className="h-4 w-4" />
                        Check keys
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPendingDelete(row)}
                        aria-label={`Revoke ${row.identifier}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Revoke ${pendingDelete?.identifier ?? ""}?`}
        description="Every request carrying this issuer will fail immediately. There is no grace period and this cannot be undone — re-registering is the only way back."
        confirmLabel="Revoke"
        pending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
      />
    </div>
  );
}

/** Where this row's verification keys come from, plus any probe result.
 *
 * Shows `resolved_jwks_url` rather than the stored fields: an issuer with no
 * explicit jwks_url still implies a well-known path, and an operator
 * debugging a 401 needs the URL the verifier will actually fetch. */
function KeySource({ row, result }: { row: TrustedSystem; result?: TrustedSystemCheckResult }) {
  return (
    <div className="space-y-1">
      {row.is_dev_bypass ? (
        <Badge variant="destructive">Unsigned — dev only</Badge>
      ) : (
        <span className="break-all font-mono text-xs text-muted-foreground">
          {row.resolved_jwks_url}
        </span>
      )}
      {result ? (
        result.error ? (
          <p className="text-xs text-destructive">{result.error}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {result.key_count} key{result.key_count === 1 ? "" : "s"}
            {result.kids.length ? ` · kid: ${result.kids.join(", ")}` : ""}
          </p>
        )
      ) : null}
    </div>
  );
}

function RegisterForm({ onDone }: { onDone: () => void | Promise<void> }) {
  const [identifier, setIdentifier] = React.useState("");
  const [name, setName] = React.useState("");
  const [jwksUrl, setJwksUrl] = React.useState("");

  const create = useMutation({
    mutationFn: () =>
      createTrustedSystem({
        identifier: identifier.trim(),
        name: name.trim(),
        // Blank means dev-bypass, which the API refuses in production. Sent as
        // null rather than "" so the intent is explicit on the wire.
        jwks_url: jwksUrl.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(`${identifier.trim()} registered`);
      setIdentifier("");
      setName("");
      setJwksUrl("");
      await onDone();
    },
    onError: (e: unknown) => toast.error(errorText(e)),
  });

  return (
    <Card className="mb-4 p-4">
      <form
        className="grid gap-4 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (identifier.trim() && name.trim()) create.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="ts-identifier">Identifier</Label>
          <Input
            id="ts-identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="myfam-prod"
          />
          {/* The single most common onboarding mistake: this is a lookup key,
              not a label, and a mismatch is indistinguishable from an unknown
              issuer on the wire. */}
          <p className="text-xs text-muted-foreground">
            Must exactly match the <code>iss</code> claim the application sends.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ts-name">Name</Label>
          <Input
            id="ts-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="myFAM Production"
          />
          <p className="text-xs text-muted-foreground">For operators. Not used in verification.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ts-jwks">JWKS URL</Label>
          <Input
            id="ts-jwks"
            value={jwksUrl}
            onChange={(e) => setJwksUrl(e.target.value)}
            placeholder="https://idp.example.com/.well-known/jwks.json"
          />
          <p className="text-xs text-muted-foreground">
            Where Aviary fetches the application&rsquo;s public keys. Leave blank only for local
            development — production refuses unsigned issuers.
          </p>
        </div>
        <div className="md:col-span-3">
          <Button type="submit" disabled={create.isPending || !identifier.trim() || !name.trim()}>
            {create.isPending ? "Registering…" : "Register"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong";
}
